/*
  Mije Seoul - cart recommendations (drawer + /cart page).

  Reads the [data-cart-reco] root that snippets/cart-reco.liquid renders from
  the shop metafield mije.cart_reco. The payload shape is the ONE contract
  in mije-data's docs/cart-reco.md (mirrored in this repo's docs/cart-reco.md):

    contexts.<ctx>  = [ "handle", ... ]   up to 20, best first (ranked
                                          nightly by a/(a+b) in the warehouse)
    scores.<ctx>    = { handle: mean }    observability only - never read here

  Liquid has already picked the context (dominant product type in the bag)
  and emitted that context's handle list. Per root:
    1. arm       - FNV-1a hash of the cart token, mod 100. Below HOLDOUT_PCT
                   (a constant 10 - the split is not in the payload) the arm
                   is 'holdout': the block renders NOTHING and emits no
                   impressions; only the once-per-session exposure event
                   fires, so the control arm is measurable. The cart token
                   is stable for the life of the cart, so the arm is sticky
                   across the session.
    2. ranking   - published order. The bandit (Thompson prior + decayed
                   evidence) runs nightly in mije-data; the theme takes the
                   list as ranked and adds no randomness of its own.
    3. filter    - skip handles already in the bag, then walk the list and
                   ask /products/<h>.js; unavailable or 404 is skipped. Stop
                   at three.
    4. render    - card with first image, price, and a one-tap Add that is a
                   NATIVE form POST to /cart/add (never XHR: the storefront
                   rate-limits /cart/add.js and a queued XHR add is exactly the
                   call that 429s). The browser lands on /cart with the line
                   added; cart attributes are untouched by an id+quantity post.
    5. events    - exactly the three events in docs/cart-reco.md, with the
                   reco_* properties only. All go through posthog.capture, so
                   they ride on the mije_ft_* super properties that
                   mije-attribution.js registers, like every other mije_*
                   event.

  The drawer re-renders through the Section Rendering API after every cart
  change, which replaces the root; a MutationObserver picks the new root up.
*/
(function () {
  'use strict';

  if (window.MijeCartReco) return;
  window.MijeCartReco = { version: 1 };

  var MAX_CARDS = 3;
  var MAX_PROBES = 8;          // /products/<h>.js lookups per render, at most
  var HOLDOUT_PCT = 10;        // constant: buckets 0-9 of 100 are the holdout arm
  var LABEL = 'Wear it together';
  var EXPOSURE_KEY = 'mije.cart_reco.exposed.v1';   // sessionStorage: once per session

  /* ------------------------------------------------------------ helpers */

  function cartToken() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)cart=([^;]+)/);
      return m ? decodeURIComponent(m[1]) : '';
    } catch (e) { return ''; }
  }

  function fnv1a(str) {
    var h = 0x811c9dc5;
    for (var i = 0; i < str.length; i++) {
      h ^= str.charCodeAt(i);
      h = Math.imul(h, 0x01000193) >>> 0;
    }
    return h >>> 0;
  }

  function money(cents) {
    try {
      if (window.MijeCurrency && typeof window.MijeCurrency.format === 'function') {
        return window.MijeCurrency.format(cents);
      }
    } catch (e) {}
    return 'S$' + (cents / 100).toFixed(2);
  }

  function sized(src, width) {
    if (!src) return '';
    var url = src.indexOf('//') === 0 ? 'https:' + src : src;
    return url + (url.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function el(tag, cls, text) {
    var node = document.createElement(tag);
    if (cls) node.className = cls;
    if (text != null) node.textContent = text;
    return node;
  }

  function session(key, value) {
    try {
      if (value === undefined) return window.sessionStorage.getItem(key);
      window.sessionStorage.setItem(key, value);
    } catch (e) { return null; }
  }

  /* ---------------------------------------------------------- posthog */

  function ph() {
    try {
      return (window.posthog && typeof window.posthog.capture === 'function') ? window.posthog : null;
    } catch (e) { return null; }
  }

  /* posthog.init() runs from the <head> loader; the official snippet queues
     calls made before the library lands. Poll briefly rather than assume an
     order, same as mije-attribution.js. No PostHog (DEV theme has no key) is
     a silent no-op. */
  function whenPostHog(fn) {
    var tries = 0;
    (function attempt() {
      if (ph()) { try { fn(ph()); } catch (e) {} return; }
      if (tries++ > 40) return;
      window.setTimeout(attempt, 250);
    })();
  }

  function capture(event, props, opts) {
    whenPostHog(function (p) {
      try { p.capture(event, props, opts || undefined); } catch (e) {}
    });
  }

  /* --------------------------------------------------------- products */

  function product(handle) {
    return window.fetch('/products/' + encodeURIComponent(handle) + '.js', {
      headers: { Accept: 'application/json' },
      credentials: 'same-origin'
    }).then(function (r) {
      if (!r.ok) return null;
      return r.json();
    }).then(function (p) {
      if (!p || !p.available) return null;
      var v = null;
      for (var i = 0; i < (p.variants || []).length; i++) {
        if (p.variants[i].available) { v = p.variants[i]; break; }
      }
      if (!v) return null;
      var img = p.featured_image || (p.images && p.images[0]) || '';
      return { handle: p.handle, title: p.title, variant: v, image: img };
    }).catch(function () { return null; });
  }

  /* ----------------------------------------------------------- render */

  function card(root, item, layout, position) {
    var addUrl = root.getAttribute('data-add-url') || '/cart/add';
    var context = root.getAttribute('data-context') || 'default';
    var variantTitle = (item.variant.title && item.variant.title !== 'Default Title')
      ? item.variant.title : 'One size';

    var form = document.createElement('form');
    form.method = 'post';
    form.action = addUrl;
    form.className = 'mj-cart-reco__form';
    form.setAttribute('data-reco-handle', item.handle);

    var id = document.createElement('input');
    id.type = 'hidden'; id.name = 'id'; id.value = String(item.variant.id);
    var qty = document.createElement('input');
    qty.type = 'hidden'; qty.name = 'quantity'; qty.value = '1';
    form.appendChild(id);
    form.appendChild(qty);

    var row = el('div', layout === 'page' ? 'mjcp-wear__card' : 'mj-cart__xsell-row');
    row.setAttribute('data-reco-card', item.handle);
    row.setAttribute('data-reco-position', String(position));

    var thumb = el('div', layout === 'page' ? 'mjcp-wear__thumb' : 'mj-cart__xsell-thumb');
    if (item.image) {
      var img = document.createElement('img');
      img.src = sized(item.image, layout === 'page' ? 250 : 200);
      img.alt = item.title;
      img.loading = 'lazy';
      img.width = layout === 'page' ? 64 : 48;
      img.height = layout === 'page' ? 84 : 62;
      thumb.appendChild(img);
    }

    var info = el('div', layout === 'page' ? 'mjcp-wear__info' : 'mj-cart__xsell-info');
    var name = el('p', layout === 'page' ? 'mjcp-wear__name' : 'mj-cart__xsell-name', item.title);
    var meta = el('p', layout === 'page' ? 'mjcp-wear__meta' : 'mj-cart__xsell-meta');
    meta.appendChild(document.createTextNode(variantTitle + ' · '));
    var price = el('span', 'mj-money', money(item.variant.price));
    price.setAttribute('data-base', String(item.variant.price));
    meta.appendChild(price);
    info.appendChild(name);
    info.appendChild(meta);

    var btn = el('button', 'mj-cart__xsell-add', 'Add');
    btn.type = 'submit';
    btn.setAttribute('data-reco-add', item.handle);
    btn.setAttribute('aria-label', 'Add ' + item.title + ' to bag');

    form.addEventListener('submit', function () {
      /* Fire-and-forget BEFORE the native post navigates. sendBeacon is the
         transport that survives a page unload; if PostHog is not loaded yet
         the event is lost rather than the add being delayed. */
      var p = ph();
      if (p) {
        try {
          p.capture('mije_cart_reco_add', {
            reco_context: context, reco_handle: item.handle
          }, { transport: 'sendBeacon', send_instantly: true });
        } catch (e) {}
      }
      btn.disabled = true;
    });

    row.appendChild(thumb);
    row.appendChild(info);
    row.appendChild(btn);
    form.appendChild(row);
    return form;
  }

  function render(root, items, layout) {
    var wrap, list;
    if (layout === 'page') {
      wrap = el('div', 'mjcp-wear');
      var head = el('div', 'mj-section-head');
      head.style.paddingBottom = '14px';
      head.style.marginBottom = '22px';
      var title = el('div', 'mj-section-head__title');
      title.appendChild(el('span', 'mj-section-head__num', '01'));
      var h2 = el('h2', null, LABEL);
      h2.style.fontSize = '26px';
      title.appendChild(h2);
      head.appendChild(title);
      wrap.appendChild(head);
      list = el('div', 'mjcp-wear__grid');
    } else {
      wrap = el('div', 'mj-cart__xsell');
      wrap.appendChild(el('p', 'mj-cart__xsell-label', LABEL));
      list = el('div', 'mj-cart__xsell-rows');
    }
    items.forEach(function (item, i) { list.appendChild(card(root, item, layout, i)); });
    wrap.appendChild(list);
    root.appendChild(wrap);
    root.hidden = false;
  }

  /* ----------------------------------------------------------- events */

  /*
    Fire once per root, and only when the block is actually on screen: the
    /cart page immediately, the drawer when its panel opens. The drawer root
    is replaced after every cart mutation, so a re-render is a new root and
    a new impression - that is intended (a new bag, a new render).

    What fires (docs/cart-reco.md, mirrored from mije-data):
      mije_cart_reco_exposure    once per SESSION, first time the block is
                                 on screen with a non-empty bag:
                                 {reco_arm: shown|holdout, reco_context}
      mije_cart_reco_impression  shown arm only: one per rendered handle
                                 {reco_context, reco_handle, reco_position}
                                 holdout emits NO impressions.
  */
  function whenVisible(root, fire) {
    var panel = root.closest('[data-cart-panel]');
    if (!panel) { fire(); return; }
    if (panel.classList.contains('is-open')) { fire(); return; }
    var mo = new MutationObserver(function () {
      if (panel.classList.contains('is-open')) { mo.disconnect(); fire(); }
    });
    mo.observe(panel, { attributes: true, attributeFilter: ['class'] });
  }

  function armEvents(root, arm, context, handles) {
    var fired = false;
    whenVisible(root, function () {
      if (fired) return;
      fired = true;
      if (!session(EXPOSURE_KEY)) {
        session(EXPOSURE_KEY, arm);
        capture('mije_cart_reco_exposure', { reco_arm: arm, reco_context: context });
      }
      if (arm === 'holdout') return;
      handles.forEach(function (h, i) {
        capture('mije_cart_reco_impression', {
          reco_context: context, reco_handle: h, reco_position: i
        });
      });
    });
  }

  /* ------------------------------------------------------------- init */

  /* The contract's candidate list is bare handles, best first. Anything
     that is not a non-empty string is dropped rather than guessed at. */
  function normalise(raw) {
    var out = [];
    if (!Array.isArray(raw)) return out;
    for (var i = 0; i < raw.length; i++) {
      if (typeof raw[i] === 'string' && raw[i]) out.push(raw[i]);
    }
    return out;
  }

  function init(root) {
    if (root.getAttribute('data-reco-state')) return;
    root.setAttribute('data-reco-state', 'pending');

    var layout = root.getAttribute('data-layout') || 'drawer';
    var context = root.getAttribute('data-context') || 'default';
    var inCart = (root.getAttribute('data-in-cart') || '').split(',').filter(Boolean);
    var candidates = [];
    try {
      var src = root.querySelector('[data-cart-reco-candidates]');
      candidates = normalise(JSON.parse(src ? src.textContent : '[]'));
    } catch (e) { candidates = []; }

    var token = cartToken() || 'no-token';
    var bucket = fnv1a(token) % 100;
    root.setAttribute('data-reco-bucket', String(bucket));

    if (bucket < HOLDOUT_PCT) {
      root.setAttribute('data-reco-state', 'holdout');
      root.setAttribute('data-reco-arm', 'holdout');
      armEvents(root, 'holdout', context, []);
      return;
    }
    root.setAttribute('data-reco-arm', 'shown');
    root.setAttribute('data-reco-ranking', 'published-order');

    var ranked = candidates
      .filter(function (h) { return inCart.indexOf(h) === -1; })
      .slice(0, MAX_PROBES);

    var picked = [];
    (function next(i) {
      if (picked.length >= MAX_CARDS || i >= ranked.length) {
        if (!document.contains(root)) return;      // root replaced mid-flight
        if (!picked.length) {
          root.setAttribute('data-reco-state', 'empty');
          return;                                    // nothing shown, no impression
        }
        render(root, picked, layout);
        root.setAttribute('data-reco-state', 'rendered');
        armEvents(root, 'shown', context, picked.map(function (p) { return p.handle; }));
        return;
      }
      product(ranked[i]).then(function (item) {
        if (item) picked.push(item);
        next(i + 1);
      });
    })(0);
  }

  function scan(node) {
    if (!node || node.nodeType !== 1) return;
    if (node.matches && node.matches('[data-cart-reco]')) init(node);
    var found = node.querySelectorAll ? node.querySelectorAll('[data-cart-reco]') : [];
    for (var i = 0; i < found.length; i++) init(found[i]);
  }

  function boot() {
    scan(document.body);
    new MutationObserver(function (records) {
      for (var i = 0; i < records.length; i++) {
        var added = records[i].addedNodes;
        for (var j = 0; j < added.length; j++) scan(added[j]);
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();
})();
